<template>
  <div class="attendance-calendar-component table-scrollable"
       :class="{'has-bulk-action-bar': selectedDays.length}">

    <bulk-action-bar
      :selected-days="selectedDays"
      :can-lock="hasUnlockedInSelection"
      :can-unlock="hasLockedInSelection"
      :can-fill-reality="canFillReality"
      :can-fill-plan="canFillPlan"
      :can-delete-reality="canDeleteReality"
      :can-delete-plan="canDeletePlan"
      :trans="bulkTrans"
      @fill="massFill"
      @delete="massDelete"
      @lock="lockSelected"
      @unlock="unlockSelected"
      @clear="deselectAllDays"
    />

    <table class="table table-att-month">
      <thead>
      <tr>
        <th class="th-day-lock" :class="{'day-select-head': true, 'day-select-head--active': selectedDays.length}">
          <div class="dropdown">
            <i class="fas fa-ellipsis-v dropdown-toggle"></i>
            <div class="dropdown-menu" aria-labelledby="dropdownMenu">
              <a class="dropdown-item" @click="selectAllDays" href="#"><i class="fa fa-check"></i>{{ trans.selectAll }}</a>
              <a class="dropdown-item" @click="selectAvailableDays" href="#"><i
                class="fa fa-check-double"></i>{{ bulkTrans.select_available }}</a>
              <a class="dropdown-item" @click="selectDaysByPlan" href="#"><i
                class="fa fa-calendar-alt"></i>{{ bulkTrans.select_by_plan }}</a>
              <a class="dropdown-item" @click="selectWorkdays" href="#"><i
                class="fa fa-briefcase"></i>{{ bulkTrans.select_workdays }}</a>
              <a class="dropdown-item dropdown-item--section-end" @click="selectUntilYesterday" href="#"><i
                class="fa fa-calendar"></i>{{ trans.selectUntilYesterday }}</a>
              <a class="dropdown-item" @click="invertSelection" href="#"><i
                class="fa fa-exchange-alt"></i>{{ bulkTrans.invert_selection }}</a>
            </div>
          </div>
        </th>
        <th class="th-date">{{ trans.date }}</th>
        <th class="th-shift-plan">{{ trans.shift_plan }}</th>
        <th class="th-flag-day p-0"></th>
        <th class="th-work-time">{{ trans.work_time }}</th>
        <th class="th-total-time">{{ trans.total_time }}</th>
        <th class="th-actions-buttons"></th>
      </tr>
      </thead>

      <tbody v-cloak>

      <template
        v-for="(day, index) in days"
      >
        <tr
          :key="index + 1"
          v-if="day.offDay"
          :class="{ 'holiday': day.offDay, 'weekend': day.isWeekend, 'is-hidden': day.workDay && day.workDay.is_hidden, 'outside-contract': day.isOutsideContract }"
        >
          <td></td>
          <td class="td-date pb-0" colspan="6">
            <span v-if="day.offDay" class="holiday">
              <i class="fas fa-star"></i>&nbsp;{{ day.offDay }}
            </span>
          </td>
        </tr>

        <tr
          :key="day.date"
          :class="{ 'weekend': day.isWeekend, 'is-hidden': day.workDay && day.workDay.is_hidden, 'holiday no-border': day.offDay, 'outside-contract': day.isOutsideContract }"
        >
          <td class="td-day-lock">
            <div class="d-flex align-items-center">
              <div class="day-select" @click.prevent="toggleSelectDay($event, day, index)">
                <input type="checkbox" :id="'checkbox' + day.date" :checked="isDayChecked(day.date)"
                       class="form-check-input">
                <label class="form-check-label" :for="'checkbox' + day.date"></label>
              </div>
              <div class="day-lock">
                <a
                  v-if="day.isLocked"
                  type="button"
                  class="btn text-success"
                  @click="toggleLocked(day, false)"
                  :class="{'loading disabled': toggleLoadingDate.some(item => item === day.date) }"
                >
                  <i class="fas fa-lock" v-if="!toggleLoadingDate.some(item => item === day.date)"></i>
                  <i class="loadingSpinner"></i>
                </a>
                <a
                  v-if="!day.isLocked"
                  type="button"
                  class="btn text-warning"
                  @click="toggleLocked(day, true)"
                  :class="{'loading disabled': toggleLoadingDate.some(item => item === day.date) }"
                >
                  <i class="fas fa-unlock" v-if="!toggleLoadingDate.some(item => item === day.date)"></i>
                  <i class="loadingSpinner"></i>
                </a>
              </div>
            </div>
          </td>

          <td class="td-date">
            <span class="date">
              <b>{{ day.date | date('DD') }}</b>
            </span>
            <span class="week-day">
              {{ day.date | date('dddd') }}
            </span>
          </td>

          <td class="td-shift-plan">
            <template v-if="day.shiftPlan && day.shiftPlan.intervals && day.shiftPlan.intervals.length">
              <div class="plan-cell">
                <div class="plan-intervals">
                  <div v-for="(interval, i) in day.shiftPlan.intervals" :key="'spi_'+i" class="plan-row">
                    <span v-if="interval.from && interval.to"
                          class="plan-interval-text badge badge-light"
                          :class="{'plan-interval-text--clickable': !day.isLocked}"
                          @click="!day.isLocked && editShiftPlan(day)"
                    >
                      <template v-if="interval.planIntervalType === workType">
                        {{ interval.from | datetime('HH:mm') }} – {{ interval.to | datetime('HH:mm') }}
                      </template>
                      <template v-else>
                        {{ interval.planIntervalTypeLabel }} {{ intervalLength(interval.from, interval.to) }} h.
                      </template>
                    </span>
                    <span v-if="day.shiftPlan.otherRestaurant" class="plan-other-restaurant">({{ day.shiftPlan.otherRestaurant }})</span>
                  </div>
                </div>
                <button
                  v-if="!day.isLocked && day.shiftPlan.id"
                  type="button"
                  class="btn-row-delete"
                  @click="deleteShiftPlan(day)"
                ><i class="far fa-trash-alt"></i></button>
              </div>
            </template>
            <button
              v-else-if="!day.isLocked"
              type="button"
              class="btn-add-inline btn-add-plan"
              @click="editShiftPlan(day)"
            ><i class="fal fa-plus"></i></button>
          </td>

          <td class="td-flag-day">
            <span
              v-if="day.flagId"
              :class="'badge badge-flag badge-'+(day.flagId !== 2 ? 'info':'danger')"
              :title="trans.flag[day.flagId]"
              @click="$refs.flagPopover.toggle($event.currentTarget, day, 'flag')"
            >
              <i class="fas fa-comment-dots fa-fw"></i>
            </span>
            <span
              v-if="day.noteId"
              class="badge badge-flag badge-warning text-white"
              :title="trans.flag[noteFlag]"
              @click="$refs.flagPopover.toggle($event.currentTarget, day, 'note')"
            >
              <i class="fas fa-comment-dots fa-fw"></i>
            </span>
          </td>

          <td class="td-work-time">
            <div class="d-flex align-items-center">
              <div class="work-cell-content">
                <template v-if="day.workDay && day.workDay.intervals && day.workDay.intervals.length">
                  <div class="work-intervals"
                       :class="{'work-intervals--clickable': !day.isLocked}"
                       @click="!day.isLocked && editWorkDay(day)"
                  >
                    <div v-for="(interval, index) in day.workDay.intervals" :class="`interval-${index + 1}`" :key="index">
                      <template v-if="interval.work_interval_type == workType">
                        <span class="badge badge-dark">
                          {{ interval.from | datetime('HH:mm') }} - {{ interval.to | datetime('HH:mm') }}
                        </span>
                        <span class="badge badge-light">
                          {{ intervalLength(interval.from, interval.to) }}&nbsp;{{ trans.hours }}
                        </span>
                      </template>
                      <template v-if="interval.work_interval_type != workType">
                        <div class="work-holiday">
                          <span class="badge badge-holiday">
                            {{ interval.work_interval_type | localizedIntervalType(workIntervalTypes) }}
                          </span>
                          <span class="badge badge-light">
                            {{ intervalLength(interval.from, interval.to) }}&nbsp;{{ trans.hours }}
                          </span>
                          <b-badge v-if='dayHasProblem(day.workDay)' variant='danger' v-b-tooltip.hover
                                   :title="trans.exclamationDayHasProblem" style="min-width:auto;">
                            <i class="fas fa-exclamation"></i>
                          </b-badge>
                        </div>
                      </template>
                    </div>
                  </div>
                  <button
                    v-if="!day.isLocked"
                    type="button"
                    class="btn-row-delete"
                    @click="deleteItem(day)"
                  ><i class="far fa-trash-alt"></i></button>
                </template>
                <button
                  v-else-if="!day.isLocked"
                  type="button"
                  class="btn-add-inline btn-add-plan"
                  @click="editWorkDay(day)"
                ><i class="fal fa-plus"></i></button>
              </div>

              <div v-if="day.workDay && getLegalChecksForDay(day.workDay).length > 0">
                <b-button :id="'popover-btn-legalcheck-day-'+day.workDay.id" variant="link"
                          @click.stop="toggleLegalChecksPopoverVisibility(index)">
                  <span class="fas fa-2x fa-fw fa-exclamation-triangle text-danger"></span>
                </b-button>
                <b-popover :show.sync="popoversStates[index]" :workDayId="day.workDay.id"
                           :target="'popover-btn-legalcheck-day-'+day.workDay.id" placement="top">
                  <template slot="title">{{ trans.legal_checks_title }}</template>
                  <ul style="padding: 0; margin:0 0.8rem;">
                    <li v-for="x in getLegalChecksForDay(day.workDay)">
                      <span class="nl2br">{{ x.message }}</span>
                    </li>
                  </ul>
                </b-popover>
              </div>
            </div>
          </td>

          <td class="td-total-time">
            <div class="total-hours">
              <template v-if="day.workDay">
                                    <span v-if="day.workDay.intervals_sum" class="badge badge-success">
                                        {{ intervalSumFormat(day.workDay.intervals_sum) }}&nbsp;{{ trans.hours }}
                                    </span>
                <span v-if="Number.parseFloat(day.workDay.number_of_night_shifts_hours) > 0" class="badge badge-midnight">
                                        <i class="fa fa-moon"></i> {{
                    Number.parseFloat(day.workDay.number_of_night_shifts_hours).toFixed(2)
                  }}
                                    </span>
              </template>
            </div>
          </td>

          <td class="td-actions-buttons">
            <div v-if="!day.isLocked && day.workDay && day.workDay.intervals && day.workDay.intervals.length > 0"
                 class="btn-group"
            >
              <button
                @click="handleChange(day)"
                type="button"
                class="btn btn-light btn-sm"
                v-if="day.workDay.number_of_all_absences_hours == 0 && day.workDay.intervals[0].work_interval_type == workType && (allowHideWorkShifts || day.workDay.is_hidden)"
              >
                <i v-if="!day.workDay.is_hidden" class="far fa-fw fa-eye text-success"></i>
                <i v-else class="far fa-fw fa-eye-slash text-danger"></i>
              </button>

              <button
                @click="showHistory(day)"
                type="button"
                class="btn btn-secondary btn-sm btn-versionning"
                :disabled="!day.isVersioned"
              >
                <i class="fal fa-fw fa-history"></i>
              </button>
            </div>

            <div v-if="!day.isLocked && (!day.workDay || !day.workDay.intervals || day.workDay.intervals.length == 0)"
                 class="btn-group"
            >
              <button
                @click="showHistory(day)"
                type="button"
                class="btn btn-secondary btn-sm btn-versionning"
                :disabled="!day.isVersioned"
              >
                <i class="fal fa-fw fa-history"></i>
              </button>
            </div>
          </td>
        </tr>
      </template>

      </tbody>
    </table>

    <day-flag-popover ref="flagPopover" :trans="trans" @edit="editWorkDay"></day-flag-popover>
  </div>
</template>

<script>
import BulkActionBar from './BulkActionBar';
import DayFlagPopover from './DayFlagPopover';
import {computeBatchImpact, batchImpactHtml} from './batchImpact';
import {formatIntervalLength} from '../../utils';

export default {
  name: 'attendance-calendar',
  components: {BulkActionBar, DayFlagPopover},
  data: function () {
    return {
      toggleLoadingDate: [],
      deleteLoadingDate: null,
      selectedDays: [],
      lastToggledIndex: null,
      popoversStates: [ // 31 items (days)
        false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false
      ]
    }
  },

  props: {
    'days': {
      type: Array
    },
    'employeeContractId': {
      type: Number
    },
    'loading': {
      type: Boolean
    },
    'deleteUrl': {
      type: String
    },
    'lockUrl': {
      type: String
    },
    'lockEmptyDayUrl': {
      type: String
    },
    'unlockUrl': {
      type: String
    },
    'massTogglelockUrl': {
      type: String
    },
    'hideUrl': {
      type: String
    },
    'unhideUrl': {
      type: String
    },
    'allowHideWorkShifts': {
      type: Boolean,
      default: true
    },
    'trans': {
      type: Object
    },
    'workIntervalTypes': {
      type: Array
    },
    'workType': {
      type: String
    },
    'modalLoadingDate': {
      type: String
    },
    'workedHolidays': {
      type: Array
    },
    'legalChecks': {
      type: Array
    },
    'shiftPlanDeleteUrl': {
      type: String
    },
    'massDestroyUrl': {
      type: String
    },
    'massPlanDestroyUrl': {
      type: String
    },
    'noteFlag': {
      type: Number
    }
  },

  created() {
  },

  watch: {
    days() {
      this.deselectAllDays();

      this.toggleLoadingDate = [];
    }
  },

  methods: {
    handleChange(day) {
      day.workDay.is_hidden = !day.workDay.is_hidden;
      this.toggleHidden(day, day.workDay.is_hidden);
    },

    editWorkDay(day) {
      this.$modal.show('edit-work-day', {day});
    },

    editShiftPlan(day) {
      this.$modal.show('edit-shift-plan', {day});
    },

    deleteShiftPlan(day) {
      const shiftPlan = day.shiftPlan;
      const _this = this;

      this.$modal.show('dialog', {
        title: _this.trans.deleteWarning.title,
        text: _this.trans.deleteWarning.text,
        buttons: [
          {title: _this.trans.deleteWarning.no},
          {
            title: '<span class="btn-dialog btn-danger">' + _this.trans.deleteWarning.ok + '<span>',
            handler: function () {
              _this.$modal.hide('dialog');
              axios.delete(_this.shiftPlanDeleteUrl + '/' + shiftPlan.id)
                .then((response) => {
                  _this.refreshCalendar();
                  _this.refreshAttendance();
                  _this.$notify({type: 'success', title: 'Success!', text: response.data.message ? response.data.message : 'Item successfully deleted.'});
                })
                .catch((error) => {
                  _this.refreshCalendar();
                  _this.refreshAttendance();
                  _this.$notify({type: 'error', title: 'Error!', text: error.response.data.message ? error.response.data.message : 'An error has occured.'});
                });
            }
          }
        ]
      });
    },

    showHistory(day) {
      this.$modal.show('work-days-history-modal', {day});
    },

    toggleHidden(day, hide) {
      const workDay = day.workDay;
      let _this = this;
      let url = null;

      if (hide) {
        url = this.hideUrl + "/" + workDay.id;
      } else {
        url = this.unhideUrl + "/" + workDay.id;
      }

      axios.post(url).then(function (response) {
        _this.refreshCalendar();
        _this.refreshAttendance();

        _this.$notify({
          type: 'success',
          title: 'Success!',
          text: response.data.message ? response.data.message : 'Item successfully updated.'
        });
      }, function (error) {
        _this.refreshCalendar();
        _this.refreshAttendance();

        _this.$notify({
          type: 'error',
          title: 'Error!',
          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
        });
      });
    },

    toggleLocked(day, lock) {
      const workDay = day.workDay;
      let _this = this;
      let url = null;

      this.toggleLoadingDate.push(day.date);

      if (lock) {
        url = workDay ? this.lockUrl + "/" + workDay.id : this.lockEmptyDayUrl + "/" + day.date + "/" + this.employeeContractId;
      } else {
        url = this.unlockUrl + "/" + workDay.id;
      }

      axios.post(url).then(function (response) {
        _this.refreshCalendar();
        _this.refreshAttendance(false);

        // _this.$notify({ type: 'success', title: 'Success!', text: response.data.message ? response.data.message : 'Item successfully updated.' });
      }, function (error) {
        _this.refreshCalendar();
        _this.refreshAttendance(false);

        _this.$notify({
          type: 'error',
          title: 'Error!',
          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
        });
        _this.toggleLoadingDate = _this.toggleLoadingDate.filter(item => item !== day.date);
      });
    },

    massToggleLock(dates, operation) {
      let _this = this;

      if (!dates.length) {
        this.$notify({type: 'warning', title: this.bulkTrans.summary_title, text: this.bulkTrans.summary_nothing});
        return;
      }

      this.toggleLoadingDate = this.toggleLoadingDate.concat(dates);

      const clearLoading = function () {
        _this.toggleLoadingDate = _this.toggleLoadingDate.filter(item => dates.indexOf(item) === -1);
      };

      axios.post(this.massTogglelockUrl, {dates: dates, operation: operation}).then(function (response) {
        _this.afterBulkAction(response, null, {attendanceLoading: false, silentSuccess: true});
      }, function (error) {
        clearLoading();
        _this.afterBulkAction(null, error, {attendanceLoading: false});
      });
    },

    deleteItem(day) {
      const workDay = day.workDay;
      let _this = this;

      var confirmText = _this.trans.deleteWarning.text;
      if (workDay.is_off_day && workDay.intervals[0].work_interval_type == 'work' && this.findWorkedHolidayPair(workDay.id)) {
        confirmText += "<p class='mt-2 text-danger'><b>" + _this.trans.deleteWarning.workedHoliday + "</b></p>";
      }

      this.$modal.show('dialog', {
        title: _this.trans.deleteWarning.title,
        text: confirmText,
        buttons: [{title: _this.trans.deleteWarning.no}, {
          title: '<span class="btn-dialog btn-danger">' + _this.trans.deleteWarning.ok + '<span>',
          handler: function handler() {
            _this.deleteLoadingDate = day.date;
            _this.$modal.hide('dialog');

            axios.delete(_this.deleteUrl + "/" + workDay.id).then(function (response) {
              _this.deleteLoadingDate = null;
              _this.refreshCalendar();
              _this.refreshAttendance();

              _this.$notify({
                type: 'success',
                title: 'Success!',
                text: response.data.message ? response.data.message : 'Item successfully deleted.'
              });
            }, function (error) {
              _this.deleteLoadingDate = null;
              _this.refreshCalendar();
              _this.refreshAttendance();

              _this.$notify({
                type: 'error',
                title: 'Error!',
                text: error.response.data.message ? error.response.data.message : 'An error has occured.'
              });
            });
          }
        }]
      });
    },

    refreshCalendar(withLoading = true) {
      this.$emit('refreshcalendar', withLoading);
    },
    refreshAttendance(withLoading = true) {
      this.$emit('refreshattendance', withLoading);
      this.$emit('refreshworkedholidays');
      this.$emit('refreshlegalchecks');
      this.$emit('refreshbalances');
    },
    isDayChecked(date) {
      return this.selectedDays.some(item => item === date);
    },

    selectableDays() {
      return this.days.filter(day => !day.isOutsideContract);
    },

    toggleSelectDay(event, day, index) {
      const selected = !this.isDayChecked(day.date);

      if (event.shiftKey && this.lastToggledIndex !== null && this.lastToggledIndex !== index) {
        const from = Math.min(this.lastToggledIndex, index);
        const to = Math.max(this.lastToggledIndex, index);

        this.days.slice(from, to + 1)
          .forEach(item => this.setDaySelected(item.date, selected));
      } else {
        this.setDaySelected(day.date, selected);
      }

      this.lastToggledIndex = index;

      // DA-313: workaround for preventing auto-scroll calendar out-of-screeen
      // caused by overflow: hidden of #app (it is auto-scrolled by some vue core functionality (focus to clicked element? or something like that)
      document.getElementById('app').scrollTop = 0;
    },

    setDaySelected(date, selected) {
      if (selected) {
        if (!this.isDayChecked(date)) {
          this.selectedDays.push(date);
        }
      } else {
        this.selectedDays = this.selectedDays.filter(item => item !== date);
      }
    },

    selectAllDays() {
      this.selectedDays = this.days.map(item => item.date);
    },
    selectAvailableDays() {
      this.selectedDays = this.selectableDays().map(item => item.date);
    },
    selectDaysByPlan() {
      this.selectedDays = this.selectableDays()
        .filter(item => item.shiftPlan && item.shiftPlan.id)
        .map(item => item.date);
    },
    selectWorkdays() {
      this.selectedDays = this.selectableDays()
        .filter(item => !item.isWeekend && !item.isOffday)
        .map(item => item.date);
    },
    selectUntilYesterday() {
      const yesterday = moment().subtract(1, 'day');
      this.selectedDays = this.selectableDays().filter(item => {
        return moment(item.date).isBefore(yesterday);
      }).map(item => item.date)
    },
    invertSelection() {
      this.selectedDays = this.days
        .filter(item => !this.isDayChecked(item.date))
        .map(item => item.date);
    },
    deselectAllDays() {
      this.selectedDays = [];
      this.lastToggledIndex = null;
    },

    massFill(mode) {
      this.$modal.show('mass-add-work-days', {
        dates: this.selectedDays.slice(),
        days: this.days,
        mode: mode
      });
    },

    massDelete(mode) {
      const _this = this;
      const impact = computeBatchImpact(this.days, this.selectedDays, 'delete', mode, this.bulkTrans);

      if (!impact.hasEligible) {
        this.$notify({type: 'warning', title: this.bulkTrans.summary_title, text: this.bulkTrans.summary_nothing});
        return;
      }

      const dates = impact.eligible.map(day => day.date);
      const url = mode === 'plan' ? this.massPlanDestroyUrl : this.massDestroyUrl;
      const payload = mode === 'plan'
        ? {dates: dates, employee_contract_id: this.employeeContractId}
        : {dates: dates};

      this.$modal.show('dialog', {
        title: this.bulkTrans.delete_confirm_title,
        text: batchImpactHtml(impact, this.bulkTrans),
        buttons: [
          {title: _this.bulkTrans.btn_cancel},
          {
            title: '<span class="btn-dialog btn-danger"><i class="far fa-trash-alt mr-2"></i>' + _this.bulkTrans.btn_delete + '</span>',
            handler: function () {
              _this.$modal.hide('dialog');

              axios.delete(url, {data: payload}).then(function (response) {
                _this.afterBulkAction(response);
              }, function (error) {
                _this.afterBulkAction(null, error);
              });
            }
          }
        ]
      });
    },

    lockSelected() {
      this.massToggleLock(this.selectedDays.slice(), 'lock');
    },
    unlockSelected() {
      this.massToggleLock(this.selectedDays.slice(), 'unlock');
    },

    /**
     * @param options.attendanceLoading pri zamykaní sa štatistiky nemenia, tak ich nenačítavame
     *                                  s loaderom - ľavý panel by len preblikol
     * @param options.silentSuccess     pri zamykaní je zmena ikony dostatočná spätná väzba,
     *                                  toast by len otravoval (upozornenia a chyby idú vždy)
     */
    afterBulkAction(response, error, options = {}) {
      const attendanceLoading = options.attendanceLoading !== false;

      this.deselectAllDays();
      this.refreshCalendar();
      this.refreshAttendance(attendanceLoading);

      if (error) {
        this.$notify({
          type: 'error',
          title: this.bulkTrans.notify_error_title,
          text: error.response && error.response.data.message
            ? error.response.data.message
            : this.bulkTrans.notify_error_text
        });
        return;
      }

      const isWarning = response.data.type === 'warning';

      if (options.silentSuccess && !isWarning) {
        return;
      }

      this.$notify({
        type: isWarning ? 'warning' : 'success',
        title: isWarning ? this.bulkTrans.summary_title : this.bulkTrans.notify_success_title,
        text: response.data.message ? response.data.message : this.bulkTrans.notify_success_text
      });
    },

    dayHasProblem(day) {
      if (day.intervals[0].work_interval_type == 'nvp-holiday' && !this.findWorkedHolidayPair(day.id, true)) {
        return true;
      }

      return false;
    },

    findWorkedHolidayPair(dayId, searchAsCompensation) {
      for (var x in this.workedHolidays) {
        var item = this.workedHolidays[x];

        if (searchAsCompensation && item.compensated_by == dayId ||
          !searchAsCompensation && item.id_day == dayId && item.compensated_by) {
          return true;
        }
      }

      return false;
    },

    intervalLength(start, end) {
      return formatIntervalLength(start, end);
    },

    intervalSumFormat(intervalsSumStr) {
      let arr = intervalsSumStr.split(":");
      return (arr[0] && arr[1] ? arr[0] + ":" + arr[1] : "(ERR)");
    },

    getLegalChecksForDay(workDay) {
      let arr = this.legalChecks.filter(x => x.workDayId === workDay.id);

      return arr ? arr : [];
    },

    toggleLegalChecksPopoverVisibility(index) {
      this.popoversStates.forEach((x, i) => {
        if (i === index) {
          this.popoversStates[i] = true;
        } else {
          this.popoversStates[i] = false;
        }
      });
    },
  },

  computed: {
    bulkTrans() {
      return Object.assign({lock: this.trans.lock, unlock: this.trans.unlock}, this.trans.bulk || {});
    },

    selectedDaysData() {
      return this.days.filter(day => this.selectedDays.some(date => date === day.date));
    },

    hasUnlockedInSelection() {
      return this.selectedDaysData.some(day => !day.isLocked);
    },

    hasLockedInSelection() {
      return this.selectedDaysData.some(day => day.isLocked);
    },

    editableSelectedDaysData() {
      return this.selectedDaysData.filter(day => !day.isOutsideContract && !day.isLocked);
    },

    canFillReality() {
      return this.editableSelectedDaysData.length > 0;
    },

    canDeleteReality() {
      return this.editableSelectedDaysData.some(day => day.workDay);
    },

    canFillPlan() {
      return this.editableSelectedDaysData.length > 0;
    },

    canDeletePlan() {
      return this.editableSelectedDaysData.some(day => day.shiftPlan && day.shiftPlan.id);
    },

    // a computed getter
    legalCheckPopoverActive: {
      // getter
      get: function (x, y, z) {
        return true;
      },
      // setter
      set: function (newValue) {
      }
    }
  },

  filters: {
    date: function date(date) {
      var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'YYYY-MM-DD';

      var date = (0, moment)(date);
      return date.isValid() ? date.format(format) : "";
    },
    datetime: function datetime(_datetime) {
      var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'YYYY-MM-DD HH:mm:ss';

      var date = (0, moment)(_datetime);
      return date.isValid() ? date.format(format) : "";
    },
    floatToTime: function floatToTime(number) {
      var date = new Date(number * 3600 /* sec per hr */
        * 1000 /* msec per sec */);
      return ('0' + date.getUTCHours()).slice(-2) + ':' +
        ('0' + date.getUTCMinutes()).slice(-2);
    },
    localizedIntervalType: function (key) {
      let item = arguments[1].find(x => x.key === key);
      return (item ? item.label : "???");
    }
  },
}
</script>


<style lang="scss">
.attendance-calendar-component.has-bulk-action-bar {
  padding-bottom: 20px;
}

.table-att-month .day-select-head .dropdown-menu .dropdown-item--section-end {
  border-bottom: 2px solid #cdd6e0;
}

.table-att-month td.td-day-lock .day-select {
  cursor: pointer;
  user-select: none;
}

.btn-square-loading,
.btn.loading.btn-square-loading {
  width: 36px;
  height: 31px;
  padding-right: 0.75rem !important;
  text-align: center;

  .loadingSpinner {
    margin-left: -8px;
    left: auto;
    right: auto;
  }
}

.btn-square-mini-loading,
.btn.loading.btn-square-mini-loading {
  width: 27px;
  height: 25px;
  padding-right: 0.75rem !important;
  text-align: center;

  .loadingSpinner {
    margin-left: -8px;
    left: auto;
    right: auto;
  }
}

.magic-switch {
  margin-left: 10px;
}

.td-work-time {
  vertical-align: middle;

  .d-flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.table-att-month {

  tr.outside-contract {
    opacity: 0.45;
  }

  td.td-shift-plan,
  td.td-work-time {
    padding: 8px 10px;
    position: relative;
  }

  td.td-shift-plan {
    max-width: 165px;
    text-align: left;

    .plan-cell {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      max-width: 275px;
    }

    .plan-intervals {
      min-width: 0;
    }

    .plan-row {
      display: flex;
      align-items: center;
      position: relative;
      z-index: 1;
      min-width: 0;
    }

    .plan-interval-text {
      background: transparent;
      color: #4273FA;
      white-space: normal;
      overflow-wrap: break-word;

      &.plan-interval-text--clickable {
        cursor: pointer;

        &:hover {
          color: #2d5de0;
        }
      }
    }

    &:hover .plan-interval-text {
      text-decoration: underline;
    }
  }

  tr.weekend td.td-shift-plan {
    .plan-interval-text {
      background: transparent;
    }
  }

  td.td-total-time {
    // dva badge (súčet + nočné) vedľa seba v riadku, nezalamovať pod seba (miesto vezme z work-time)
    .total-hours {
      display: inline-flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 4px;
      white-space: nowrap;
    }
  }

  td.td-work-time {
    .work-cell-content {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-left: 5px;
    }

    .work-intervals {
      position: relative;
      z-index: 1;
      &.work-intervals--clickable {
        cursor: pointer;
      }

      // dvojica badge (čas intervalu + celkový čas) spojená do jedného pill – bez medzery medzi nimi;
      // flex (nie inline-flex), aby viaceré intervaly boli pod sebou, nie vedľa seba
      > div {
        display: flex;
        align-items: center;
        width: fit-content;
      }

      .badge-dark {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      .badge-light {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    .badge-light {
      font-size: 15px;
      padding: 7px 10px;
    }

    // na hover nezvýrazňujeme celý stĺpec, ale samotné badge – zachováme ich základnú farbu
    // a len ju stmavíme (čierna → tmavšia čierna, sivá → tmavšia sivá, fialová → tmavšia fialová …)
    &:hover .work-intervals .badge {
      box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.12);
    }
  }

  td.td-shift-plan,
  td.td-work-time {
    .btn-row-delete {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 28px;
      flex-shrink: 0;
      border-radius: 4px;
      border: none;
      background: #FE3F61;
      color: white;
      font-size: 11px;
      cursor: pointer;
      opacity: 0;
      margin-left: auto;
      clip-path: inset(0 0 0 100% round 4px);
      transition: clip-path 0.2s ease, opacity 0.2s ease;

      i { pointer-events: none; }

      &:hover { background-color: #e02d4f; }
    }

    .btn-add-inline {
      position: absolute;
      inset: 12px 12px 12px 0;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: #4273FA;
      font-size: 14px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s ease, background 0.15s ease;

      i { pointer-events: none; }
    }

    .btn-add-plan {
      cursor: pointer;
      border: 1px solid #4273FA;
      border-radius: 6px;
      width: 30px;
      height: 30px;
      top: 50%;
      bottom: auto;
      left: 10px;
      right: auto;
      transform: translateY(-50%);
      font-size: 12px;
      background: transparent;
      transition: opacity 0.15s ease;

      &:hover,
      &:focus,
      &:active {
        background: transparent;
      }
    }
  }

  td.td-flag-day {
    white-space: nowrap;

    .badge-flag + .badge-flag {
      margin-left: 4px;
    }
  }

  td.td-shift-plan:hover .btn-add-plan,
  td.td-work-time:hover .btn-add-plan {
    opacity: 1;
    background: transparent;
  }

  // dotykové zariadenia nemajú hover – add-tlačidlo (plán/realita) zobrazíme vždy,
  // inline trash naopak skryjeme (delete je na touchi presunutý do editačného modálu)
  @media (hover: none) {
    td.td-shift-plan .btn-add-plan,
    td.td-work-time .btn-add-plan {
      opacity: 1;
    }

    td.td-shift-plan .btn-row-delete,
    td.td-work-time .btn-row-delete {
      display: none;
    }
  }

  td.td-shift-plan .btn-row-delete {
    margin-left: 0;
    z-index: 1;
    width: auto;
    height: auto;
    padding: 0;
    background: transparent;
    color: #FE3F61;
    font-size: 15px;
    clip-path: none;
    transition: none;

    &:hover {
      background: transparent;
      color: #e02d4f;
    }
  }

  td.td-shift-plan:hover .btn-row-delete {
    opacity: 1;
  }

  td.td-work-time .btn-row-delete {
    margin-left: 0;
    z-index: 1;
    width: auto;
    height: auto;
    padding: 0;
    background: transparent;
    color: #FE3F61;
    font-size: 15px;
    clip-path: none;
    transition: none;

    &:hover {
      background: transparent;
      color: #e02d4f;
    }
  }

  td.td-work-time:hover .btn-row-delete {
    opacity: 1;
  }

}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/show/AttendanceCalendar.vue